import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faLocationArrow } from '@fortawesome/pro-regular-svg-icons/faLocationArrow';
import { faPencilAlt } from '@fortawesome/pro-regular-svg-icons/faPencilAlt';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import { RecentSearch } from '@propertypal/shared/src/hooks/useRecentSearches';
import CloseToPin from '@propertypal/shared/src/resources/icons/search/pin-filled.svg';
import { SearchFormNlpResult, Token } from '@propertypal/shared/src/types/search';
import platform from '@propertypal/shared/src/utils/platform';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import groupTokensByType, { Suggestion } from '@propertypal/shared/src/utils/search/groupTokensByType';
import { detectTouchDevice } from '@propertypal/web-app/src/hooks/useTouchDevice';
import React, {
  FunctionComponent,
  KeyboardEventHandler,
  ReactNode,
  ReactEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { useTheme } from 'styled-components';
import { animateInputPlaceholder } from '../animation/textAnimation';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Text } from '../typography';
import {
  Container,
  Input,
  SuggestionList,
  Header,
  Item,
  ItemImage,
  LoadingBox,
  SearchMobileIcon,
  FormattedInput,
  InputWrapper,
  SearchInputIcon,
} from './SearchInput.style';

const PLACEHOLDERS = platform.select(
  [
    '3 bed in Belfast under £280k',
    'Rental in West Belfast, max £800pm',
    'Detached with sea views',
    'Rental in West Belfast, max £800pm',
  ],
  [
    'Donaghadee, Millisle, Bangor',
    'BT7, BT8, BT9',
    '3 bed Houses in Belfast or Lisburn',
    'Apartments near Jordanstown Train Station',
    'Detached houses in North Coast Area',
    '3 bed in Belfast under 250k',
  ],
);

interface Props {
  containerStyle?: React.CSSProperties;
  disableSavedSearches?: boolean;
  onChangeText: (text: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  loading?: boolean;
  value: string;
  nlpData?: SearchFormNlpResult;
  onSuggestionClick?: (token: Suggestion) => void;
  onLocationClick?: (position: 'inline' | 'suggestion') => void;
  onTravelTimeClick?: (position: 'inline' | 'suggestion') => void;
  onDrawSearchClick?: (position: 'inline' | 'suggestion') => void;
  onFocus?: () => void;
  onCloseToClick?: () => void;
  suggestionsOffset?: number;
  suggestionsLeftOffset?: number;
  isHomePage?: boolean;
  placeholder?: string;
  recentSearches?: RecentSearch[];
  onRecentSearchClick?: (searchText: string, filters: Filters) => void;
  onRemoveRecentSearch?: (search: RecentSearch) => void;
  showMobileSearchIcon?: boolean;
  hideSearchIcons?: boolean;
}

const SUGGESTIONS_ID = 'search-suggestions';

const formatTokens = (value: string, tokens?: Token[]) => {
  if (!tokens?.length) {
    return <p>{value}</p>;
  }

  const sortedMatches = [...tokens].sort((a, b) => a.start - b.start);

  const elements: ReactNode[] = [];
  let currentIndex = 0;

  sortedMatches.forEach((token) => {
    if (token.searchTerm && value.includes(token.asTyped)) {
      if (token.start > currentIndex) {
        elements.push(<React.Fragment key={token.start}>{value.slice(currentIndex, token.start)}</React.Fragment>);
      }

      elements.push(<span key={token.asTyped}>{token.asTyped}</span>);

      currentIndex = token.trimmedEnd;
    }
  });

  if (currentIndex < value.length) {
    elements.push(<React.Fragment key="text-end">{value.slice(currentIndex)}</React.Fragment>);
  }

  return <p>{elements}</p>;
};

const SearchInput: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [suggestion, setSuggestion] = useState(0);
  const [focused, setFocused] = useState(false);
  const sections = props.nlpData ? groupTokensByType(props.nlpData, props.disableSavedSearches) : [];
  const lastClick = useRef<number>(0);
  const searchRefFallback = useRef<HTMLInputElement>(null);
  const searchInputRef = props.inputRef || searchRefFallback;
  const mouseDown = useRef<boolean>(false);
  const dragging = useRef<boolean>(false);
  const currentSelection = useRef<number[] | null>(null);
  const tokens = props.nlpData?.phrase.words;
  const [inputScrollLeft, setInputScrollLeft] = useState(0);

  const handleClick: ReactEventHandler = (e) => {
    const now = new Date().getTime();

    if (tokens && now - lastClick.current > 300 && !dragging.current) {
      const input = e.target as HTMLInputElement;

      if (
        currentSelection.current != null &&
        input.selectionStart != null &&
        currentSelection.current[0] <= input.selectionStart &&
        currentSelection.current[1] > input.selectionStart
      ) {
        currentSelection.current = null;
      } else {
        const newToken = tokens.find((token) => {
          return (
            input.selectionStart != null &&
            token.start <= input.selectionStart &&
            token.trimmedEnd > input.selectionStart
          );
        });

        if (newToken) {
          input.setSelectionRange(newToken.start, newToken.trimmedEnd);
          currentSelection.current = [newToken.start, newToken.trimmedEnd];
        } else {
          currentSelection.current = null;
        }
      }
    } else {
      currentSelection.current = null;
    }

    lastClick.current = now;
  };

  const scrollToIndex = (index: number) => {
    const element = document.getElementById(`search-suggestion-${index}`);
    const scrollElement = document.getElementById(SUGGESTIONS_ID);
    if (element && scrollElement) {
      scrollElement.scrollTo(0, element.offsetTop - 200);
    }
  };

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSuggestion(suggestion + 1);
      scrollToIndex(suggestion + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSuggestion(Math.max(0, suggestion - 1));
      scrollToIndex(Math.max(0, suggestion - 1));
    } else if (event.key === 'Enter') {
      let token: Suggestion | null = null;

      sections.forEach((section) => {
        section.data.forEach((mToken) => {
          if (mToken.canonicalIndex === suggestion) {
            token = mToken;
          }
        });
      });

      if (token && props.onSuggestionClick) {
        event.preventDefault();
        props.onSuggestionClick(token);
        setFocused(false);
      }
    } else if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      const token = tokens?.find((t) => t.searchTerm && input.selectionStart === t.trimmedEnd);

      if (token) {
        event.preventDefault();
        props.onChangeText(`${props.value.slice(0, token.start)}${props.value.slice(token.trimmedEnd)}`);
      }
    }
  };

  const handleKeyEvent = (e: KeyboardEvent) => {
    const el = searchInputRef.current;

    if (el && e.code === 'Slash' && document.activeElement !== el) {
      e.preventDefault();
      el.focus();
    }

    if (el && document.activeElement === el && e.code === 'Enter') {
      setFocused(false);
    }
  };

  const handlePointerDown = (e: MouseEvent) => {
    // @ts-ignore
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setFocused(false);
    }
  };

  const handlePointerClick = (e: MouseEvent) => {
    // On Desktop click event is inconsistent. Some browsers return the target from pointerdown and some from pointerup
    // On Mobile, pointerdown is not triggered when hitting search on the virtual keyboard. It does however trigger a
    // click event on the submit button of the parent form, which we look for here
    if (detectTouchDevice()) handlePointerDown(e);
  };

  useEventListener('pointerdown', handlePointerDown);
  useEventListener('click', handlePointerClick);
  useEventListener('keydown', handleKeyEvent);

  useEffect(() => {
    setSuggestion(0);
  }, [props.nlpData]);

  useEffect(() => {
    if (!props.placeholder) {
      let i = -1;
      let timeout: any;
      let animation: any;

      const nextPlaceholder = () => {
        i += 1;
        if (i >= PLACEHOLDERS.length) i = 0;

        animation = animateInputPlaceholder(PLACEHOLDERS[i], searchInputRef.current, 1500, () => {
          timeout = setTimeout(nextPlaceholder, 2500);
        });
      };

      nextPlaceholder();

      return () => {
        clearTimeout(timeout);
        clearTimeout(animation.frame);
      };
    }

    return () => {};
  }, []);

  return (
    <Container ref={containerRef} style={props.containerStyle}>
      <InputWrapper>
        <Input
          aria-label="Property search text"
          placeholder={props.placeholder}
          ref={searchInputRef}
          onClick={handleClick}
          onChange={(event) => props.onChangeText(event.target.value)}
          onKeyDown={handleKeyDown}
          onMouseDown={() => {
            mouseDown.current = true;
            dragging.current = false;
          }}
          onMouseMove={() => {
            if (mouseDown.current) dragging.current = true;
          }}
          onMouseUp={() => {
            mouseDown.current = false;
          }}
          data-testid="searchInput"
          onFocus={() => {
            setFocused(true);
            if (props.onFocus) props.onFocus();
          }}
          value={props.value}
          aria-autocomplete="both"
          aria-owns={SUGGESTIONS_ID}
          aria-activedescendant={`search-suggestion-${suggestion}`}
          type="search"
          onScroll={(event) => {
            // @ts-ignore
            setInputScrollLeft(event.target.scrollLeft);
          }}
        />

        <FormattedInput style={{ left: inputScrollLeft * -1 }}>{formatTokens(props.value, tokens)}</FormattedInput>
      </InputWrapper>

      {!props.hideSearchIcons && (
        <>
          {!props.loading && props.onDrawSearchClick && (
            <SearchInputIcon
              aria-label="Draw a search"
              title="Draw a search"
              onClick={() => props.onDrawSearchClick && props.onDrawSearchClick('inline')}
              type="button"
              data-testid="drawSearchButton"
            >
              <FontAwesomeIcon icon={faPencilAlt} size={22} style={{ marginTop: 2 }} />
            </SearchInputIcon>
          )}

          {!props.loading && props.onTravelTimeClick && (
            <SearchInputIcon
              aria-label="Search by travel time"
              title="Search by travel time"
              onClick={() => props.onTravelTimeClick && props.onTravelTimeClick('inline')}
              type="button"
              data-testid="travelTimeButton"
            >
              <FontAwesomeIcon icon={faCarSide} size={22} style={{ marginTop: 2 }} />
            </SearchInputIcon>
          )}

          {!props.loading && props.onLocationClick && (
            <SearchInputIcon
              aria-label="Search by current location"
              title="Search by current location"
              onClick={() => props.onLocationClick && props.onLocationClick('inline')}
              type="button"
            >
              <FontAwesomeIcon icon={faLocationArrow} size={22} style={{ marginTop: 2 }} />
            </SearchInputIcon>
          )}
        </>
      )}

      {!props.loading && platform.type === 'propertynews' && props.showMobileSearchIcon && (
        <SearchMobileIcon aria-label="Search" title="Search" type="submit" onClick={() => setFocused(false)}>
          <FontAwesomeIcon icon={faSearch} size={22} style={{ marginTop: 2 }} />
        </SearchMobileIcon>
      )}

      {props.loading && (
        <LoadingBox>
          <ScaleLoader color={theme.backgroundLight} data-testid="inputLoading" height={25} />
        </LoadingBox>
      )}

      {focused && (
        <SuggestionList
          id={SUGGESTIONS_ID}
          role="listbox"
          offset={props.suggestionsOffset}
          leftOffset={props.suggestionsLeftOffset}
          data-testid="suggestionList"
          isHomePage={props.isHomePage}
        >
          {!sections.length && (
            <>
              <Header>Suggestions</Header>

              {props.onCloseToClick && (
                <Item
                  data-testid="closeToButton"
                  onClick={() => {
                    if (props.onCloseToClick) {
                      props.onCloseToClick();
                      setFocused(false);
                    }
                  }}
                >
                  <CloseToPin style={{ width: 18, marginRight: 15 }} />

                  <Text>Close to Search</Text>
                </Item>
              )}

              {props.onDrawSearchClick && (
                <Item
                  onClick={() => {
                    if (props.onDrawSearchClick) {
                      props.onDrawSearchClick('suggestion');
                      setFocused(false);
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPencilAlt}
                    color={theme.textDark}
                    style={{ fontSize: 18, marginRight: 15 }}
                  />

                  <Text>Draw a Search</Text>
                </Item>
              )}

              {props.onTravelTimeClick && (
                <Item
                  onClick={() => {
                    if (props.onTravelTimeClick) {
                      props.onTravelTimeClick('suggestion');
                      setFocused(false);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCarSide} color={theme.textDark} style={{ fontSize: 18, marginRight: 15 }} />

                  <Text>Search By Travel Time</Text>
                </Item>
              )}

              <Item
                data-testid="locationButton"
                onClick={() => {
                  if (props.onLocationClick) {
                    props.onLocationClick('suggestion');
                    setFocused(false);
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faLocationArrow}
                  color={theme.textDark}
                  style={{ fontSize: 18, marginRight: 15 }}
                />

                <Text>Search by Current Location</Text>
              </Item>
            </>
          )}

          {!!sections.length &&
            sections.map((section) => (
              <React.Fragment key={section.key}>
                <Header>{section.key}</Header>

                {section.data.map((token) => (
                  <Item
                    id={`search-suggestion-${token.canonicalIndex}`}
                    focused={token.canonicalIndex === suggestion}
                    key={token.id}
                    data-testid={`suggestion${token.id}`}
                    onClick={() => {
                      if (props.onSuggestionClick) {
                        props.onSuggestionClick(token);
                        setFocused(false);
                      }
                    }}
                  >
                    {!!token.image && <ItemImage src={token.image} data-testid={`suggestion${token.id}Image`} />}

                    <div>
                      {!!token.heading && (
                        <Text fontSize={18} fontWeight="bold" align="left" data-testid={`suggestion${token.id}Heading`}>
                          {token.heading}
                        </Text>
                      )}

                      <Text align="left" data-testid={`suggestion${token.id}Text`}>
                        {token.text}
                      </Text>

                      {!!token.subText && (
                        <Text align="left" data-testid={`suggestion${token.id}SubText`}>
                          {token.subText}
                        </Text>
                      )}
                    </div>
                  </Item>
                ))}
              </React.Fragment>
            ))}

          {!!props.recentSearches?.length && (
            <>
              <Header>Recent Searches</Header>

              {props.recentSearches.map((search) => (
                <Item
                  key={search.description.complete}
                  onClick={() => {
                    if (props.onRecentSearchClick) {
                      props.onRecentSearchClick(search.text, search.filters);
                      setFocused(false);
                    }
                  }}
                >
                  <div>
                    <Text color={theme.textDark}>{search.description.stem}</Text>

                    {!!search.description.details && (
                      <Text fontSize={14} mt={4} color={theme.textLight}>
                        {search.description.details}
                      </Text>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      if (props.onRemoveRecentSearch) {
                        e.stopPropagation();
                        props.onRemoveRecentSearch(search);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} size={18} color={theme.textDark} />
                  </button>
                </Item>
              ))}
            </>
          )}
        </SuggestionList>
      )}
    </Container>
  );
};

export default SearchInput;
